import React, {useContext, useEffect, useMemo} from 'react';
import {useFieldArray, useFormContext} from 'react-hook-form';
import {IPurchaseModel} from '@src/@interfaces/models/Purchase/IPurchaseModel';
import {ListConfigFieldTypes, ListConfigNamedColumn} from '@src/@types/components/List';
import {IFormFieldItemPropsContext} from '@src/@interfaces/contexts/IFormFieldItemPropsContext';
import {FormFieldDesignContext} from '@src/contexts/FormFieldDesignContext';
import ConfirmableButton from '@src/components/ConfirmableButton/ConfirmableButton';
import ControlledList from '@src/components/ControlledList/ControlledList';
import {ControlledListProps} from '@src/@types/components/ControlledList';
import FormField, {Props} from '@src/components/Form/FormField';
import {FormFieldItemPropsContext} from '@src/contexts/FormFieldItemPropsContext';
import {FormTabsContext} from '@src/contexts/FormTabsContext';
import {MaterialField} from '@views/purchases/fields/materials/MaterialField';
import {PriceField} from '@views/purchases/fields/materials/PriceField';
import {QuantityField} from '@views/purchases/fields/materials/QuantityField';
import classnames from '@src/helpers/classnames';
import FormErrorMessage from '@src/components/Form/FormErrorMessage';
import {TableCaption} from '@views/purchases/fields/materials/TableCaption';

const columns = [
    {
        name: 'id',
        label: '#',
        type: ListConfigFieldTypes.CUSTOM,
        invisible: true,
    },
    {
        name: 'material_id',
        label: 'PurchasesShow.fields.materials.material',
        type: ListConfigFieldTypes.CUSTOM,
    },
    {
        name: 'price',
        label: 'PurchasesShow.fields.materials.price',
        type: ListConfigFieldTypes.NUMBER,
    },
    {
        name: 'quantity',
        label: 'PurchasesShow.fields.materials.quantity',
        type: ListConfigFieldTypes.NUMBER,
    },
];

function getCustomColumnComponent(config: ListConfigNamedColumn) {
    switch (config.name) {
        case 'material_id':
            return MaterialField;

        case 'price':
            return PriceField;

        case 'quantity':
            return QuantityField;
    }

    return null;
}

function ActualForm(props: Props<IPurchaseModel>) {
    const tabsContext = useContext(FormTabsContext);
    const [designContext] = useContext(FormFieldDesignContext);
    const {control, getFieldState, formState} = useFormContext<IPurchaseModel>();
    const {fields, append, remove} = useFieldArray({
        control,
        name: props.fieldName as 'materials',
        keyName: 'autoGeneratedUuid',
    });

    const tabName = props.tabName;
    useEffect(() => {
        if (tabsContext && tabName) {
            tabsContext.setBadge(tabName, {
                color: 'primary',
                label: String(fields.length),
            });
        }
    }, [tabsContext, tabName, fields.length]);

    const isDisabled = props.forceDisabled || designContext.fieldsAreDisabled || designContext.fieldsAreReadOnly;
    const [listContentProps, formFieldArrayContextValues] = useMemo(() => {
        const listContentProps: ControlledListProps<IPurchaseModel>['listContentProps'] = {
            tableCaptionNode: <TableCaption/>,
            tableHeadProps: {
                additionalControlNode: <button
                        type="button"
                        className="btn btn-sm btn-outline-success me-1"
                        disabled={isDisabled}
                        onClick={() => append({
                            id: null,
                            material_id: undefined,
                            price: 0,
                            quantity: 1,
                        })}
                >
                    <i className="fa fa-plus"/>
                </button>,
            },
            tableBodyProps: {
                renderControls(record, index) {
                    if (record?.id) {
                        return <ConfirmableButton
                                size="sm"
                                color="danger"
                                disabled={isDisabled}
                                onClick={() => remove(index)}
                        >
                            <i className="fa fa-times"/>
                        </ConfirmableButton>;
                    }

                    return <button
                            type="button"
                            className="btn btn-sm btn-warning"
                            disabled={isDisabled}
                            onClick={() => remove(index)}
                    >
                        <i className="fa fa-times"/>
                    </button>;
                }
            },
        };

        const formFieldArrayContextValues: IFormFieldItemPropsContext<IPurchaseModel> = {
            control,
            fieldName: props.fieldName,
            formName: props.formName,
            tabName: props.tabName,
            model: props.model,
            modelOptions: props.modelOptions,
            config: props.config,
            forceReadonly: props.config.readOnly === true || props.forceReadonly === true,
            forceDisabled: props.config.disabled === true || props.forceDisabled === true,
        };

        return [listContentProps, formFieldArrayContextValues];
    }, [isDisabled, props, control, append, remove]);

    const isInvalid = getFieldState(props.fieldName, formState).invalid;

    return <FormFieldItemPropsContext.Provider value={formFieldArrayContextValues}>
        <ControlledList<IPurchaseModel>
                uniqueListName={`${props.formName}@${props.fieldName}--contacts-list`}
                controllerName={props.formName}
                records={fields}
                columns={columns}
                withoutSearchField={true}
                keepInParentBox={true}
                listContentProps={listContentProps}
                listContextValues={{getCustomColumnComponent}}
                bordered={true}
                className={classnames('mb-1', {
                    'is-invalid border-danger': isInvalid
                })}
        />
        <FormErrorMessage fieldName={props.fieldName} forceDisplaying/>
    </FormFieldItemPropsContext.Provider>;
}

export default function PurchaseMaterialsField<Model extends IPurchaseModel = IPurchaseModel>(props: Props<Model>) {
    return <FormField {...props} withoutLayout={true} fieldElement={ActualForm as any}/>;
}
